import { graphql, useStaticQuery } from 'gatsby'
import type React from 'react'
import OpenGraphImage from '../../../images/site-wide/open-graph-protcol-image.jpg'

interface SeoProps {
  /**
   * Page title - if not provided, falls back to site title from metadata
   */
  title?: string

  /**
   * Page description for SEO and social sharing
   */
  description: string

  /**
   * Page path relative to the site URL (e.g., "/blog/my-post")
   */
  pathname: string

  /**
   * Type of the page content for Open Graph (e.g., "website", "article")
   */
  contentType: string

  /**
   * Custom image for social sharing
   */
  socialImage: {
    /**
     * Is the image hosted on the same domain as the site?
     */
    isLocal?: boolean

    /**
     * Image URL (can be absolute or relative if isLocal is true)
     */
    url?: string
  }

  /**
   * Language of the page content
   */
  language?: 'en' | 'ja'

  /**
   * Additional meta tags to include
   */
  children?: React.ReactNode
}

/**
 * SEO component for Gatsby Head API that handles page metadata, including
 * title, description, social tags, and language localization.
 *
 * @example
 * // Basic usage in a page component
 * export const Head = () => <Seo pathname="/about" />
 *
 * @example
 * // With custom values
 * export const Head = () => (
 *   <Seo
 *     title="About Us"
 *     description="Learn about our company"
 *     pathname="/about"
 *     contentType="website"
 *     language="en"
 *   />
 * )
 */
export const Seo = ({
  title,
  description,
  pathname,
  contentType = 'website',
  socialImage,
  language,
  children,
}: SeoProps) => {
  const { siteUrl } = useMetadata()

  // Use props or fallback to defaults
  const seo = {
    title,
    description,
    url: `${siteUrl}${pathname}`,
    contentType,
  }

  // Handle image URL construction
  const imageEntity = socialImage || { isLocal: true, url: null }
  const socialImageUrl = imageEntity.url
    ? `${imageEntity.isLocal ? siteUrl : ''}${imageEntity.url}`
    : `${siteUrl}${OpenGraphImage}`

  // Generate localized paths
  const englishPath = language === 'en' ? pathname : `/en${pathname}`
  const japanesePath = language === 'ja' ? pathname : pathname.replace(/^\/en/, '')

  // Log SEO data in development for debugging
  if (process.env.NODE_ENV === 'development') {
    console.debug('SEO Data:', {
      title: seo.title,
      description: seo.description,
      url: seo.url,
      image: socialImageUrl,
      contentType: seo.contentType,
      language,
    })
  }

  return (
    <>
      <title>{seo.title}</title>
      <html lang={language} />
      <meta charSet="UTF-8" />
      <meta name="description" content={seo.description} />

      {/* Language alternates for localization */}
      <link rel="alternate" hrefLang="en" href={`${siteUrl}${englishPath}`} />
      <link rel="alternate" hrefLang="ja" href={`${siteUrl}${japanesePath}`} />
      <link rel="alternate" hrefLang="x-default" href={`${siteUrl}${japanesePath}`} />

      {/* Open Graph / Facebook */}
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content={seo.contentType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={socialImageUrl} />
      <meta property="og:locale" content={language === 'en' ? 'en_US' : 'ja_JP'} />
      <meta property="og:locale:alternate" content={language === 'en' ? 'ja_JP' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@karte_io" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={socialImageUrl} />

      {/* Fonts and scripts */}
      <link rel="canonical" href={seo.url} />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700;900&display=swap"
      />
      <script src="https://cdn-blocks.karte.io/73836f0a7f9b2161cb78d5295dfc21c7/builder.js" />

      {/* Additional meta tags */}
      {children}
    </>
  )
}

/**
 * Interface for site metadata returned from Gatsby's GraphQL layer
 */
interface SiteMetadata {
  title: string
  titleTemplate?: string
  description: string
  siteUrl: string
}

/**
 * Custom hook to retrieve site metadata from Gatsby's GraphQL layer
 *
 * @returns SiteMetadata object with complete typing
 */
export const useMetadata = (): SiteMetadata => {
  const data = useStaticQuery<{
    site: {
      siteMetadata: SiteMetadata
    }
  }>(graphql`
      query Metadata {
        site {
          siteMetadata {
            title
            titleTemplate
            description
            siteUrl
          }
        }
      }
    `)

  // Type assertion to ensure data.site is defined
  if (!data?.site?.siteMetadata) {
    throw new Error('Site metadata is not available')
  }

  return data.site.siteMetadata
}

export const useTemplateTitle = (title: string): string => {
  const { titleTemplate } = useMetadata()
  return `${title} ${titleTemplate}`
}
